import React, {FC, MouseEventHandler, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Layer} from '../../../../../../libs/shared/ui/src/components/layers/layer';
import {minimumMultiViewContentHeight} from './mutliViewHelpers';

/*
 * Constants.
 */

const topBarHeightOffset = 52; // px.
const parentPadding = 16; // px.
const mouseMoveOffset = 10; // px.
const resizerHeight = 20; // px.

/*
 * Props.
 */

interface MultiViewResizerProps {
  updateSidepanelMultiViewFrameHeights: HandlerOf<ReadonlyArray<number>>;
  onSizeUpdate: HandlerOf<number>;
}

/*
 * Style.
 */

interface StyledMultiViewResizerWrapperDivProps {
  $isDragging: boolean;
}

const StyledMultiViewResizerWrapperDiv = styled.div<StyledMultiViewResizerWrapperDivProps>`
  padding: 2px 8px;
  margin: 6px 0;
  border-radius: 8px;
  cursor: ns-resize;

  ${(p) =>
    p.$isDragging &&
    css`
      background: ${p.theme.palette.blue.shade20};
    `};
  &:hover {
    background: ${(p) => p.theme.palette.blue.shade20};
  }
`;

const StyledMultiViewResizerDiv = styled.div`
  height: 4px;
  width: 100%;
  margin: auto;
  max-width: 136px;
  background: ${(p) => p.theme.greys.shade60};
  border-radius: 4px;
  pointer-events: none;
`;

const StyledResizeOverlayDiv = styled.div`
  height: 100%;
  cursor: ns-resize;
`;

/*
 * Component.
 */

export const MultiViewResizer: FC<MultiViewResizerProps> = (props) => {
  const {updateSidepanelMultiViewFrameHeights, onSizeUpdate} = props;
  const [isDragging, setIsDragging] = useState(false);
  const newSizeRef = useRef<number>();

  const onResizeMouseDown = () => {
    setIsDragging(true);
    newSizeRef.current = undefined;
  };

  const onLayerMouseUp = () => {
    setIsDragging(false);

    if (newSizeRef.current) {
      updateSidepanelMultiViewFrameHeights(computeUpdatedFrameHeights(newSizeRef.current));
      // Need to dispatch a resize event as the calendar is listening for it.
      window.dispatchEvent(new Event('resize'));
    }
  };

  const onLayerMouseMove: MouseEventHandler = (event) => {
    if (!isDragging) {
      return;
    }

    const parentHeight = window.innerHeight - topBarHeightOffset - parentPadding;
    const mouseDragLocation = event.pageY - topBarHeightOffset - parentPadding - mouseMoveOffset;
    const minimumPercentage = computeMinimumPercentage(parentHeight);
    const maxPercentage = computeMaximumPercentage(parentHeight);
    const mousePercentageFromTop = computeMousePercentage(mouseDragLocation, parentHeight);

    const newSizeConstrained = Math.max(minimumPercentage, Math.min(maxPercentage, mousePercentageFromTop));
    onSizeUpdate(newSizeConstrained);
    newSizeRef.current = newSizeConstrained;
  };

  return (
    <>
      <StyledMultiViewResizerWrapperDiv $isDragging={isDragging} onMouseDown={onResizeMouseDown}>
        <StyledMultiViewResizerDiv />
      </StyledMultiViewResizerWrapperDiv>
      {isDragging && (
        <Layer>
          <StyledResizeOverlayDiv
            onMouseUp={onLayerMouseUp}
            onMouseLeave={onLayerMouseUp}
            onMouseMove={onLayerMouseMove}
          />
        </Layer>
      )}
    </>
  );
};

/*
 * Helpers.
 */

function computeUpdatedFrameHeights(newValue: number) {
  // TODO: If we want to add more sections, this doesn't work since it defaults to 0 index. This should loop through
  //       all options and only update the correct one.
  return [newValue];
}

function computeMinimumPercentage(parentHeight: number) {
  return roundNumberToTwoDecimals(minimumMultiViewContentHeight / parentHeight);
}

function computeMaximumPercentage(parentHeight: number) {
  return 100 - roundNumberToTwoDecimals((minimumMultiViewContentHeight + resizerHeight) / parentHeight);
}

function computeMousePercentage(eventY: number, parentHeight: number) {
  return roundNumberToTwoDecimals(eventY / parentHeight);
}

function roundNumberToTwoDecimals(value: number) {
  return Math.round(value * 10000) / 100;
}
